/* ============================================================
   iap.ts — RevenueCat / StoreKit service.
   ------------------------------------------------------------
   RevenueCat is the single source of truth for entitlements.
   No other module is allowed to compute "is this user Pro?" on
   its own — every read goes through the cached CustomerInfo
   returned by the RevenueCat SDK, or its localStorage fallback
   when offline.

   IMPORTANT — import style
   ------------------------
   We use a STATIC top-of-file import for the RevenueCat plugin.
   A previous version used a dynamic import with a @vite-ignore
   directive which caused Vite to skip bundling the module
   entirely; at runtime in the WKWebView the literal package
   string had no module resolver and the import threw, leaving
   the SDK un-initialised and the RC dashboard at zero customers.

   The plugin has a built-in web stub that no-ops on non-iOS,
   so it's safe to import at the top of the module — every call
   site here also gates on `isIOSNative()` belt-and-braces.

   Lifecycle
   ---------
   1. `initIap()` is called once at App boot from main.tsx, BEFORE
      React renders.
   2. `identifyIapUser(uid)` / `logoutIapUser()` are called from
      the EntitlementProvider whenever the Supabase session
      changes so RevenueCat's `appUserID` stays aligned with our
      auth identity.
   3. `getCachedEntitlement()` / `subscribeEntitlement(cb)` are the
      read APIs.
   4. `purchase()`, `restorePurchases()`, `openManageSubscriptions()`
      are the write/intent APIs.

   Offline behaviour
   -----------------
   The last successfully fetched entitlement state is cached in
   localStorage. If `getCustomerInfo()` errors on startup, we
   serve the cached state so a returning Pro user is not silently
   downgraded. The cache TTL is 14 days.

   Configuration
   -------------
   - VITE_REVENUECAT_IOS_API_KEY: defaults to the production iOS
     Public SDK key below.
   - VITE_DEBUG_OVERLAY=1 enables RevenueCat's verbose log level.
   ============================================================ */

import { Capacitor } from '@capacitor/core';
import {
  Purchases,
  LOG_LEVEL,
  type CustomerInfo,
  type PurchasesOffering,
  type PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import { iapLog } from './iapLogger';

/* ── Constants ───────────────────────────────────────────── */

/** Entitlement identifier as configured in the RevenueCat dashboard. */
export const ENTITLEMENT_ID = 'CADtomie Intraop Pro';

/** App-store product identifiers per platform. RevenueCat hides the
 *  platform difference behind a single `offering.monthly` /
 *  `offering.annual` accessor — these constants are only used as
 *  best-effort fallback matchers when the typed accessor doesn't
 *  resolve a package. */
export const PRODUCT_IDS = {
  ios: {
    monthly: 'com.cadtomie.intraop.pro.monthly',
    annual:  'com.cadtomie.intraop.pro.annual',
  },
  android: {
    // Google Play product IDs as configured in the Play Console
    // and mirrored in RevenueCat. The `:monthly` / `:annual`
    // suffix is the base-plan ID Play attaches to subscription
    // identifiers since the new subscription model.
    monthly: 'cadtomie_pro_monthly:monthly',
    annual:  'cadtomie_pro_annual:annual',
  },
} as const;

/** Production iOS Public SDK key (safe to ship — designed for client use). */
const DEFAULT_IOS_API_KEY = 'appl_sGiRgCucpNrgBJRhLraFVRgFoLk';

/** Production Android Public SDK key (safe to ship — public SDK
 *  keys are designed for client embedding, same as the iOS one
 *  above). Override per environment via
 *  `VITE_REVENUECAT_ANDROID_API_KEY` if you need a sandbox build. */
const DEFAULT_ANDROID_API_KEY = 'goog_IThOwnelKyxOjCaquTyNlfYugcD';

/** localStorage key for the offline entitlement cache. */
const CACHE_KEY = 'cadtomie.iap.entitlement.v1';

/** Max age the cache is considered authoritative when offline. */
const CACHE_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

/* ── Types ───────────────────────────────────────────────── */

export type PlanPeriod = 'monthly' | 'annual';

export interface IapPackage {
  identifier: string;
  period: PlanPeriod;
  priceString: string;
  pricePerMonth?: string;
  productId: string;
}

export interface EntitlementState {
  isPro: boolean;
  willRenew: boolean;
  expiresAt: Date | null;
  productId: string | null;
  /** Apple subscription management URL — open this to surface the
   *  native iOS subscription management sheet. Null when no subscription
   *  exists or RevenueCat couldn't resolve it. */
  managementURL: string | null;
  /** Source of the state.
   *  • 'sdk'           — fresh from the RevenueCat SDK
   *  • 'cache'         — offline cache hit, will be replaced when SDK
   *                      responds
   *  • 'default'       — no info yet, hasn't loaded
   *  • 'web-free'      — non-native platform (PWA / desktop), Pro
   *                      features are granted automatically as part
   *                      of the free Educational web experience
   *  • 'native-error'  — native shell tried to configure RevenueCat
   *                      and failed (bad key, network, no Play
   *                      Billing). UI should surface a "service
   *                      temporarily unavailable" hint with a retry
   *                      affordance — DO NOT silently treat as Pro.
   */
  source: 'sdk' | 'cache' | 'default' | 'web-free' | 'native-error';
  at: number;
}

export type PurchaseResult =
  | { kind: 'success'; state: EntitlementState }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string; code?: string };

const DEFAULT_STATE: EntitlementState = {
  isPro: false,
  willRenew: false,
  expiresAt: null,
  productId: null,
  managementURL: null,
  source: 'default',
  at: 0,
};

/* Pro-state used on non-iOS platforms (web / PWA / desktop).
   ---------------------------------------------------------
   The web build at intraop.cadtomie.com is intentionally free
   — Apple's StoreKit doesn't exist outside the iOS app, and
   the friction of "either subscribe via iOS or contact us" is
   bad for a doctor who just wants to try the tool. So we treat
   every signed-in web user as Pro and let them use all the
   measurement modules.

   Importantly this does NOT affect the iOS app: `isIOSNative()`
   gates every SDK interaction, and on iOS we never set this
   state. iOS keeps StoreKit + RevenueCat as the sole source of
   truth. */
const WEB_FREE_PRO_STATE: EntitlementState = {
  isPro: true,
  willRenew: false,
  expiresAt: null,
  productId: 'web-free',
  managementURL: null,
  source: 'web-free',
  at: 0,
};

/* Used when the RevenueCat SDK fails to initialise on a native
   shell (typically Android with a wrong/missing goog_ key or no
   Play Billing on the device). Importantly isPro = false — we
   refuse to fake an entitlement when we don't know the truth. The
   UI uses `source === 'native-error'` to render a recoverable
   error state with a retry button. */
const NATIVE_ERROR_STATE: EntitlementState = {
  isPro: false,
  willRenew: false,
  expiresAt: null,
  productId: null,
  managementURL: null,
  source: 'native-error',
  at: 0,
};

/* ── Platform check ──────────────────────────────────────── */

function isIOSNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

function isAndroidNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/** True on EITHER iOS or Android native shells — used to gate
 *  every RevenueCat SDK call. Web stays on the free-Pro path. */
function isStoreNative(): boolean {
  return isIOSNative() || isAndroidNative();
}

/** Return the right RevenueCat API key for the current platform.
 *  iOS uses `appl_...`, Android uses `goog_...`. Both can be
 *  overridden via env (VITE_REVENUECAT_IOS_API_KEY /
 *  VITE_REVENUECAT_ANDROID_API_KEY). */
function platformApiKey(): string {
  if (isAndroidNative()) {
    return (
      (import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY as string | undefined)
      || DEFAULT_ANDROID_API_KEY
    );
  }
  return (
    (import.meta.env.VITE_REVENUECAT_IOS_API_KEY as string | undefined)
    || DEFAULT_IOS_API_KEY
  );
}

/* ── Internal state ──────────────────────────────────────── */

let _initialized = false;
let _initPromise: Promise<void> | null = null;
let _currentState: EntitlementState = DEFAULT_STATE;
const _listeners = new Set<(s: EntitlementState) => void>();

function _setState(next: EntitlementState): void {
  _currentState = next;
  iapLog.debug('entitlement state updated', {
    isPro: next.isPro,
    source: next.source,
    productId: next.productId,
    willRenew: next.willRenew,
    expiresAt: next.expiresAt?.toISOString(),
  });
  for (const cb of _listeners) {
    try { cb(next); } catch (err) { iapLog.warn('listener threw', { err: String(err) }); }
  }
}

/* ── Cache helpers ───────────────────────────────────────── */

interface CachedEntitlement {
  v: 1;
  isPro: boolean;
  willRenew: boolean;
  expiresAt: string | null;
  productId: string | null;
  managementURL: string | null;
  at: number;
}

function _readCache(): EntitlementState | null {
  try {
    const raw = typeof localStorage !== 'undefined'
      ? localStorage.getItem(CACHE_KEY)
      : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedEntitlement;
    if (parsed.v !== 1) return null;
    if (Date.now() - parsed.at > CACHE_TTL_MS) {
      iapLog.info('cache stale — discarding');
      return null;
    }
    return {
      isPro: parsed.isPro,
      willRenew: parsed.willRenew,
      expiresAt: parsed.expiresAt ? new Date(parsed.expiresAt) : null,
      productId: parsed.productId,
      managementURL: parsed.managementURL ?? null,
      source: 'cache',
      at: parsed.at,
    };
  } catch (err) {
    iapLog.warn('cache read failed', { err: String(err) });
    return null;
  }
}

function _writeCache(state: EntitlementState): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const payload: CachedEntitlement = {
      v: 1,
      isPro: state.isPro,
      willRenew: state.willRenew,
      expiresAt: state.expiresAt ? state.expiresAt.toISOString() : null,
      productId: state.productId,
      managementURL: state.managementURL,
      at: state.at,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch (err) {
    iapLog.warn('cache write failed', { err: String(err) });
  }
}

/* ── CustomerInfo → EntitlementState ─────────────────────── */

function _parseCustomerInfo(info: CustomerInfo | null | undefined): EntitlementState {
  const activeKeys = info?.entitlements?.active
    ? Object.keys(info.entitlements.active)
    : [];
  iapLog.debug('parsing CustomerInfo', {
    originalAppUserId: info?.originalAppUserId,
    activeEntitlementIds: activeKeys,
    lookingFor: ENTITLEMENT_ID,
  });

  const managementURL = info?.managementURL ?? null;
  const ent = info?.entitlements?.active?.[ENTITLEMENT_ID];
  if (!ent) {
    return {
      ...DEFAULT_STATE,
      managementURL,
      source: 'sdk',
      at: Date.now(),
    };
  }
  return {
    isPro: true,
    willRenew: !!ent.willRenew,
    expiresAt: ent.expirationDate ? new Date(ent.expirationDate) : null,
    productId: ent.productIdentifier ?? null,
    managementURL,
    source: 'sdk',
    at: Date.now(),
  };
}

/* ── Public API ──────────────────────────────────────────── */

/** Initialise the SDK. Safe to call multiple times. */
export function initIap(opts: { userId?: string } = {}): Promise<void> {
  if (_initPromise) return _initPromise;
  _initPromise = (async () => {
    iapLog.info('initIap called', {
      hasUserId: !!opts.userId,
      platform: Capacitor.getPlatform(),
      isNative: Capacitor.isNativePlatform(),
    });

    // Seed in-memory state from the offline cache so the very first
    // render of <EntitlementProvider> has the right answer even before
    // the SDK has called home.
    const cached = _readCache();
    if (cached) {
      iapLog.info('seeded from offline cache', { isPro: cached.isPro });
      _setState(cached);
    }

    if (!isStoreNative()) {
      iapLog.info('non-store platform — granting free Pro for web', {
        platform: Capacitor.getPlatform(),
      });
      // Promote signed-in web users to Pro automatically. See
      // WEB_FREE_PRO_STATE comment for the rationale.
      _setState({ ...WEB_FREE_PRO_STATE, at: Date.now() });
      _initialized = true;
      return;
    }

    const apiKey = platformApiKey();
    const isPlaceholderAndroidKey =
      isAndroidNative() && apiKey === DEFAULT_ANDROID_API_KEY;

    if (isPlaceholderAndroidKey) {
      // Loud warning so the developer notices in the device console —
      // but DO try the SDK anyway. Letting RevenueCat itself surface
      // an auth error gives us much better diagnostics than a silent
      // bypass, and prevents masquerading as "free Pro" on a build
      // that's supposed to be monetised.
      iapLog.warn(
        'Android API key is the placeholder. The SDK will attempt to ' +
          'configure with it, which will fail with an auth error. Set ' +
          'VITE_REVENUECAT_ANDROID_API_KEY or hardcode the goog_ key in ' +
          'iap.ts:DEFAULT_ANDROID_API_KEY before shipping a Play build.',
      );
    }

    try {
      const verbose =
        import.meta.env.DEV || import.meta.env.VITE_DEBUG_OVERLAY === '1';

      iapLog.info('setting log level', {
        level: verbose ? 'DEBUG' : 'INFO',
      });
      await Purchases.setLogLevel({
        level: verbose ? LOG_LEVEL.DEBUG : LOG_LEVEL.INFO,
      });

      iapLog.info('calling Purchases.configure', {
        platform: Capacitor.getPlatform(),
        apiKeyPrefix: apiKey.slice(0, 5),
        apiKeyTail: apiKey.slice(-6),
        apiKeyLength: apiKey.length,
        appUserID: opts.userId ?? '(anonymous)',
      });
      await Purchases.configure({
        apiKey,
        appUserID: opts.userId ?? null,
      });
      iapLog.info('SDK configured successfully', {
        platform: Capacitor.getPlatform(),
      });

      // Real-time entitlement updates (renewal, expiration, sandbox
      // subscription state changes pushed by the store).
      Purchases.addCustomerInfoUpdateListener((customerInfo: CustomerInfo) => {
        const activeIds = customerInfo?.entitlements?.active
          ? Object.keys(customerInfo.entitlements.active)
          : [];
        iapLog.debug('CustomerInfo update received', {
          originalAppUserId: customerInfo?.originalAppUserId,
          activeEntitlementIds: activeIds,
        });
        const next = _parseCustomerInfo(customerInfo);
        _writeCache(next);
        _setState(next);
      });
      iapLog.info('CustomerInfo listener attached');

      // First authoritative read. This is also what creates the customer
      // record on RevenueCat's backend.
      await refreshCustomerInfo();
    } catch (err) {
      iapLog.error('SDK init failed — entering native-error fallback', {
        platform: Capacitor.getPlatform(),
        err: String(err),
        stack: err instanceof Error ? err.stack : undefined,
      });
      // On native shells, mark the state as `native-error` so the UI
      // can show a clear retry affordance. Do NOT promote to free-Pro
      // here — that hides the real problem and causes the
      // "Beta (free)" masquerade we just removed.
      _setState({ ...NATIVE_ERROR_STATE, at: Date.now() });
    } finally {
      _initialized = true;
      iapLog.info('initIap complete', { initialized: _initialized });
    }
  })();
  return _initPromise;
}

/** Tie the RevenueCat user to the Supabase user. */
export async function identifyIapUser(userId: string): Promise<void> {
  if (!_initialized) {
    iapLog.warn('identifyIapUser before init — deferring through initIap');
    await initIap({ userId });
    return;
  }
  if (!isStoreNative()) {
    iapLog.debug('identifyIapUser skipped on non-iOS', { userId });
    return;
  }
  try {
    iapLog.info('calling Purchases.logIn', { userId });
    const result = await Purchases.logIn({ appUserID: userId });
    iapLog.info('logIn returned', {
      userId,
      created: result?.created,
      activeEntitlementIds: result?.customerInfo?.entitlements?.active
        ? Object.keys(result.customerInfo.entitlements.active)
        : [],
    });
    if (result?.customerInfo) {
      const next = _parseCustomerInfo(result.customerInfo);
      _writeCache(next);
      _setState(next);
    } else {
      await refreshCustomerInfo();
    }
  } catch (err) {
    iapLog.error('logIn failed', {
      err: String(err),
      stack: err instanceof Error ? err.stack : undefined,
      userId,
    });
  }
}

export async function logoutIapUser(): Promise<void> {
  if (!isStoreNative()) return;
  try {
    iapLog.info('calling Purchases.logOut');
    await Purchases.logOut();
    iapLog.info('logOut succeeded');
    _setState({ ...DEFAULT_STATE, source: 'sdk', at: Date.now() });
    try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
  } catch (err) {
    // logOut throws if the user is already anonymous — non-fatal.
    iapLog.debug('logOut returned non-fatal error', { err: String(err) });
  }
}

/** Re-read customer info from RevenueCat. */
export async function refreshCustomerInfo(): Promise<EntitlementState> {
  if (!isStoreNative()) return _currentState;
  try {
    iapLog.info('calling Purchases.getCustomerInfo');
    const { customerInfo } = await Purchases.getCustomerInfo();
    iapLog.info('getCustomerInfo returned', {
      originalAppUserId: customerInfo?.originalAppUserId,
      activeEntitlementIds: customerInfo?.entitlements?.active
        ? Object.keys(customerInfo.entitlements.active)
        : [],
    });
    const next = _parseCustomerInfo(customerInfo);
    _writeCache(next);
    _setState(next);
    return next;
  } catch (err) {
    iapLog.warn('getCustomerInfo failed — keeping current state', {
      err: String(err),
      currentSource: _currentState.source,
    });
    return _currentState;
  }
}

export function getCachedEntitlement(): EntitlementState {
  return _currentState;
}

export function subscribeEntitlement(cb: (s: EntitlementState) => void): () => void {
  _listeners.add(cb);
  try { cb(_currentState); } catch { /* swallow */ }
  return () => { _listeners.delete(cb); };
}

/* ── Offerings & packages ────────────────────────────────── */

export async function getAvailablePackages(): Promise<IapPackage[]> {
  if (!isStoreNative()) {
    iapLog.debug('getAvailablePackages on non-store platform — returning []');
    return [];
  }

  const platform = Capacitor.getPlatform();
  const ids = platform === 'android' ? PRODUCT_IDS.android : PRODUCT_IDS.ios;

  try {
    iapLog.info('calling Purchases.getOfferings', { platform });
    const { current, all } = await Purchases.getOfferings();
    iapLog.info('getOfferings returned', {
      platform,
      currentOfferingId: current?.identifier ?? null,
      allOfferingIds: Object.keys(all ?? {}),
      currentPackageCount: current?.availablePackages?.length ?? 0,
    });

    if (!current) {
      iapLog.warn('no current offering — check RevenueCat dashboard "Current Offering" flag');
      return [];
    }

    // Dump full package detail so a misconfigured product ID is
    // visible directly in the log line. On Android the productId
    // will look like "cadtomie_pro_monthly:monthly" — that's the
    // Play Console new-subscription format and is expected.
    iapLog.info('current offering packages', {
      offeringId: current.identifier,
      packages: current.availablePackages?.map((p) => ({
        rcIdentifier: p.identifier,
        productId: p.product?.identifier,
        priceString: p.product?.priceString,
        pricePerMonth: (p.product as any)?.pricePerMonth,
        currencyCode: (p.product as any)?.currencyCode,
      })),
    });

    const out: IapPackage[] = [];
    const monthly = _pickPackage(current, '$rc_monthly', ids.monthly, 'monthly');
    const annual = _pickPackage(current, '$rc_annual', ids.annual, 'annual');

    if (monthly) out.push(_mapPackage(monthly, 'monthly'));
    if (annual)  out.push(_mapPackage(annual,  'annual'));

    if (out.length === 0) {
      iapLog.warn('offering has no monthly/annual packages mapped', {
        platform,
        expectedProductIds: ids,
        availableIds: current.availablePackages?.map((p) => p.identifier),
        availableProductIds: current.availablePackages?.map((p) => p.product?.identifier),
      });
    } else {
      iapLog.info('packages mapped', {
        platform,
        count: out.length,
        mapped: out.map((p) => ({
          period: p.period,
          productId: p.productId,
          price: p.priceString,
        })),
      });
    }
    return out;
  } catch (err) {
    iapLog.error('getOfferings failed', {
      err: String(err),
      stack: err instanceof Error ? err.stack : undefined,
    });
    return [];
  }
}

function _pickPackage(
  offering: PurchasesOffering,
  rcId: string,
  productId: string,
  period: PlanPeriod,
): PurchasesPackage | undefined {
  // Prefer the typed accessor (offering.monthly / offering.annual)
  // since RevenueCat guarantees those map to $rc_monthly / $rc_annual.
  const typed = period === 'monthly' ? offering.monthly : offering.annual;
  if (typed) return typed;
  // Fallback: match by RC package identifier or by product identifier.
  return offering.availablePackages?.find(
    (p) => p.identifier === rcId || p.product?.identifier === productId,
  );
}

function _mapPackage(pkg: PurchasesPackage, period: PlanPeriod): IapPackage {
  return {
    identifier: pkg.identifier,
    period,
    productId: pkg.product?.identifier ?? '',
    priceString: pkg.product?.priceString ?? '',
    pricePerMonth: (pkg.product as any)?.pricePerMonth ?? undefined,
  };
}

/* ── Purchase / restore / manage ─────────────────────────── */

export async function purchase(pkgIdentifier: string): Promise<PurchaseResult> {
  if (!isStoreNative()) {
    return { kind: 'error', message: 'Purchases are only available in the iOS app.' };
  }

  iapLog.info('purchase initiated', { pkgIdentifier });

  try {
    const { current } = await Purchases.getOfferings();
    if (!current) {
      iapLog.error('purchase aborted — no current offering');
      return { kind: 'error', message: 'No subscription offering available right now.' };
    }
    const pkg = current.availablePackages?.find(
      (p) => p.identifier === pkgIdentifier,
    );
    if (!pkg) {
      iapLog.error('purchase aborted — package not found', {
        pkgIdentifier,
        available: current.availablePackages?.map((p) => p.identifier),
      });
      return { kind: 'error', message: 'Selected plan is unavailable.' };
    }

    const result = await Purchases.purchasePackage({ aPackage: pkg });
    iapLog.info('purchase completed', {
      productIdentifier: result?.productIdentifier,
    });
    const state = _parseCustomerInfo(result.customerInfo);
    _writeCache(state);
    _setState(state);
    return { kind: 'success', state };
  } catch (err: any) {
    const code: string | undefined = err?.code ?? err?.errorCode;
    const userCancelled =
      !!err?.userCancelled
      || code === 'PURCHASE_CANCELLED'
      || /cancel/i.test(String(err?.message ?? ''));

    if (userCancelled) {
      iapLog.info('purchase cancelled by user');
      return { kind: 'cancelled' };
    }
    iapLog.error('purchase failed', { err: String(err), code });
    const message = err instanceof Error ? err.message : String(err);
    return { kind: 'error', message, code };
  }
}

export async function restorePurchases(): Promise<EntitlementState> {
  if (!isStoreNative()) return _currentState;

  iapLog.info('restore initiated');
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    const next = _parseCustomerInfo(customerInfo);
    _writeCache(next);
    _setState(next);
    iapLog.info('restore completed', { isPro: next.isPro });
    return next;
  } catch (err) {
    iapLog.error('restore failed', { err: String(err) });
    return _currentState;
  }
}

/** Open the platform-native subscription management sheet.
 *
 *  RevenueCat exposes a `managementURL` on CustomerInfo that resolves
 *  to the right URL per store:
 *    • iOS    → apps.apple.com/account/subscriptions  (iOS deep-link)
 *    • Android → play.google.com/store/account/subscriptions
 *  Falling back to a sensible default when managementURL is null
 *  (e.g. user has no subscription yet — they still want to see the
 *  management page). */
export async function openManageSubscriptions(): Promise<void> {
  const fallback = isAndroidNative()
    ? 'https://play.google.com/store/account/subscriptions'
    : 'https://apps.apple.com/account/subscriptions';
  const url = _currentState.managementURL || fallback;

  iapLog.info('opening manage subscriptions', {
    platform: Capacitor.getPlatform(),
    via: _currentState.managementURL ? 'managementURL' : 'fallback',
    url,
  });

  if (isStoreNative()) {
    // iOS/Android both intercept their respective store URLs and
    // present the system sheet over the app — no browser exit.
    window.location.href = url;
    return;
  }
  // Web fallback — open in a new tab.
  window.open(url, '_blank');
}
